import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { app, pages } from '@microsoft/teams-js'; // Using the imported 'app' and HostClientType for context check
import {
  FluentProvider,
  webLightTheme,
  webDarkTheme,
  teamsHighContrastTheme,
} from '@fluentui/react-components'; // Removed Button as it's not used here
import { Theme } from '@radix-ui/themes';
import toast, { Toaster } from 'react-hot-toast'; // toast is default export
import { ReactFlowProvider } from 'reactflow';

import ProtectedRoutes from './ProtectedRoutes';
import PublicRoutes from './PublicRoutes';
import DataProvider from './context/DataProvider';
import WebDataProvider from './context/WebDataProvider';
import BotsDataProvider from './context/BotsDataProvider';
import { WebSocketProvider } from './context/WebSocketProvider';
import './App.css';
import { BotSessionsProvider } from './context/BotSessionsProvider';
import { WorkflowExecutionsProvider } from './context/WorkflowExecutionsProvider';
import { EmailsProvider } from './context/EmailsProvider';
import { useTeams } from './context/TeamsProvider';
import { McpProvider } from './context/McpProvider';
import { AgentProvider } from './context/AgentProvider';
import { TaskPlanProvider } from './context/TaskPlanProvider';
import { NotificationProvider } from './context/NotificationProvider';
import { ChangeRequestProvider } from './context/ChangeRequestProvider';
import { ResourceAllocationProvider } from './context/ResourceAllocationProvider';
import DebugOverlay from './components/DebugOverlay';
import SecurityGate from './components/SecurityGate';
import { useDarkMode } from './hooks/useDarkMode';

// Your App ID from the manifest
const TEAMS_APP_ID = process.env.REACT_APP_TEAMS_APP_ID;

const AppContent = () => {
  useDarkMode();
  // Renamed App to AppContent to use hooks inside Router context
  const [teamsTheme, setTeamsTheme] = useState(webLightTheme);
  const { isTeamsInitialized, setIsTeamsInitialized } = useTeams();

  const [userIdToken, setUserIdToken] = useState(() =>
    localStorage.getItem('userToken'),
  );
  const [projectIdToken, setProjectIdToken] = useState(() =>
    localStorage.getItem('projectToken'),
  );
  const navigate = useNavigate();
  const location = useLocation();

  const onLogin = (
    user_id_token,
    received_user_id,
    project_id_token = null,
  ) => {
    // Assuming onLogin might receive user_id too
    setUserIdToken(user_id_token);
    localStorage.setItem('userToken', user_id_token);
    // If user_id is also important for app state beyond JWT, handle it here
    // navigate("/projects");
    // --- NEW LOGIC: Check for project_token to determine navigation ---
    if (project_id_token) {
      // This is a new signup. Set the project token and navigate directly to the chat page.
      setProjectIdToken(project_id_token);
      localStorage.setItem('projectToken', project_id_token);
    }

    const targetPageId = project_id_token ? 'chat' : 'projects';
    const targetEndpoint = project_id_token ? '/chat' : '/projects';

    if (isTeamsInitialized) {
      // Attempt to get current context to see if we're already on the target tab
      app
        .getContext()
        .then((context) => {
          const currentTabEntityId = context.page.id;
          if (currentTabEntityId === targetPageId) {
            // Already on the target tab, force a refresh
            navigate(targetEndpoint);
          } else {
            pages
              .navigateToApp({
                appId: TEAMS_APP_ID,
                pageId: targetPageId,
              })
              .then(() => {
                toast.success(`Navigated to ${targetEndpoint} successfully!`);
              })
              .catch((error) => {
                toast.error(
                  `Error navigating to ${targetEndpoint}: ${error.message}`,
                );
              });
          }
        })
        .catch((error) => {
          // If getContext fails, fallback to just trying to navigate
          pages
            .navigateToApp({
              appId: TEAMS_APP_ID,
              pageId: targetPageId,
            })
            .then(() => {
              toast.success(`Navigated to ${targetEndpoint} successfully!`);
            })
            .catch((navError) => {
              toast.error(
                `Error navigating to ${targetEndpoint}: ${navError.message}`,
              );
            });
        });
    } else {
      // Optional: If already on /projects in browser, force re-render or use other state logic
      if (location.pathname === targetEndpoint) {
        navigate(targetEndpoint, {
          replace: true,
          state: { timestamp: Date.now() },
        }); // Helps trigger re-render in some cases
      } else {
        navigate(targetEndpoint);
      }
    }
  };

  const onProjectAccess = (project_id_token) => {
    setProjectIdToken(project_id_token);
    localStorage.setItem('projectToken', project_id_token);
    // navigate("/chat");
    const targetPageId = 'chat';
    // const targetSubPageId = "generalChannel"; // Optional

    if (isTeamsInitialized) {
      if (pages.currentApp.isSupported()) {
        pages.currentApp
          .navigateTo({
            pageId: targetPageId,
            // subPageId: targetSubPageId,
          })
          .then(() => {
            toast.success('Navigated to chat successfully!');
          })
          .catch((error) => {
            toast.error(`Error navigating to chat: ${error.message}`);
          });
      } else {
        pages
          .navigateToApp({
            appId: TEAMS_APP_ID,
            pageId: targetPageId,
            // subPageId: targetSubPageId,
          })
          .then(() => {
            toast.success('Navigated to chat successfully!');
          })
          .catch((error) => {
            toast.error(`Error navigating to chat: ${error.message}`);
          });
      }
    } else {
      navigate('/chat');
    }
  };

  const handleStorageChange = () => {
    // This might be for cross-tab sync, ensure it's used if needed
    setUserIdToken(localStorage.getItem('userToken'));
    setProjectIdToken(localStorage.getItem('projectToken'));
  };

  useEffect(() => {
    app
      .initialize()
      .then(() => {
        setIsTeamsInitialized(true);

        // Notify Teams that your app's main content has loaded.
        app.notifyAppLoaded(); // Use the imported 'app'

        app
          .getContext()
          .then((context) => {
            // Use the imported 'app'
            if (context && context.theme) {
              switch (context.theme) {
                case 'dark':
                  setTeamsTheme(webDarkTheme);
                  break;
                case 'contrast':
                  setTeamsTheme(teamsHighContrastTheme);
                  break;
                default:
                  setTeamsTheme(webLightTheme);
                  break;
              }
            } else {
              setTeamsTheme(webLightTheme);
            }
          })
          .catch((error) => {
            toast.error(`Error getting Teams context: ${error.message}`);
          });

        app.registerOnThemeChangeHandler((theme) => {
          // Use the imported 'app'
          switch (theme) {
            case 'dark':
              setTeamsTheme(webDarkTheme);
              break;
            case 'contrast':
              setTeamsTheme(teamsHighContrastTheme);
              break;
            default:
              setTeamsTheme(webLightTheme);
              break;
          }
        });
      })
      .catch((error) => {
        setIsTeamsInitialized(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array

  // Optional: Loading state while Teams SDK initializes, if you want to prevent rendering LoginDemo too soon.
  //   if (window.microsoftTeams && !isTeamsInitialized) { // Only show loading if trying to init Teams
  //    return <div>Initializing Teams App integration...</div>;
  //   }

  return (
    <FluentProvider theme={teamsTheme}>
      <Theme>
        <Toaster
          position="top-right"
          toastOptions={{
            success: {
              // icon: "✅",
              style: {
                background: '#18181b',
                color: '#d4d4d8',
                border: '1px solid #27272a',
                borderRadius: '0.5rem',
                padding: '1rem',
              },
            },
            error: {
              icon: '❌',
              style: {
                background: '#18181b',
                color: '#d4d4d8',
                border: '1px solid #27272a',
                borderRadius: '0.5rem',
                padding: '1rem',
              },
            },
          }}
        />
        <SecurityGate>
          {userIdToken && projectIdToken ? (
            <WebSocketProvider userGoogleId={projectIdToken}>
              <DataProvider key={`${projectIdToken}-${userIdToken}`}>
                <WebDataProvider>
                  <BotsDataProvider>
                    <BotSessionsProvider>
                      <WorkflowExecutionsProvider>
                        <ReactFlowProvider>
                          <EmailsProvider>
                            <McpProvider>
                              <AgentProvider>
                                <TaskPlanProvider>
                                  <NotificationProvider>
                                    <ChangeRequestProvider>
                                      <ResourceAllocationProvider>
                                        <ProtectedRoutes
                                          userIdToken={userIdToken}
                                          projectIdToken={projectIdToken}
                                          onLogin={onLogin}
                                          onProjectAccess={onProjectAccess}
                                          handleStorageChange={
                                            handleStorageChange
                                          }
                                          isTeamsInitialized={
                                            isTeamsInitialized
                                          }
                                        />
                                      </ResourceAllocationProvider>
                                    </ChangeRequestProvider>
                                  </NotificationProvider>
                                </TaskPlanProvider>
                              </AgentProvider>
                            </McpProvider>
                          </EmailsProvider>
                        </ReactFlowProvider>
                      </WorkflowExecutionsProvider>
                    </BotSessionsProvider>
                  </BotsDataProvider>
                </WebDataProvider>
              </DataProvider>
            </WebSocketProvider>
          ) : (
            <PublicRoutes
              key={`${projectIdToken}-${userIdToken}`}
              userIdToken={userIdToken}
              projectIdToken={projectIdToken}
              onLogin={onLogin}
              onProjectAccess={onProjectAccess}
              handleStorageChange={handleStorageChange}
              isTeamsInitialized={isTeamsInitialized}
            />
          )}
          <DebugOverlay />
        </SecurityGate>
      </Theme>
    </FluentProvider>
  );
};

const App = () => {
  return <AppContent />;
};

export default App;
